import React, { useState, useEffect, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, TrendingUp, TrendingDown, Target, Award, BarChart3, Filter, Download, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const leagueLabels = {
  la_liga: 'La Liga',
  premier_league: 'Premier League',
  bundesliga: 'Bundesliga',
  serie_a: 'Serie A',
  ligue_1: 'Ligue 1',
  champions_league: 'Champions League',
  mls: 'MLS',
  liga_mx: 'Liga MX',
};

export default function Stats() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [basicStats, setBasicStats] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [bankrollEvolution, setBankrollEvolution] = useState(null);
  const [verifiedPicks, setVerifiedPicks] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [modelInsights, setModelInsights] = useState(null);
  const [modelInsightsLoading, setModelInsightsLoading] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    league: '',
    market: '',
    minOdds: '',
    maxOdds: '',
    month: '',
    channel: ''
  });

  const fetchAllStats = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [basic, evolution, monthly] = await Promise.all([
        fetch(`${API_URL}/api/stats`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/stats/bankroll-evolution?days=90`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/stats/monthly-summary`, { headers }).then(r => r.json())
      ]);

      setBasicStats(basic);
      setBankrollEvolution(evolution);
      setMonthlyStats(monthly);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllStats();
  }, [fetchAllStats]);

  const fetchAdvancedStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.league) params.append('league', filters.league);
      if (filters.market) params.append('market', filters.market);
      if (filters.minOdds) params.append('min_odds', filters.minOdds);
      if (filters.maxOdds) params.append('max_odds', filters.maxOdds);
      if (filters.month) params.append('month', filters.month);
      if (filters.channel) params.append('channel', filters.channel);

      const response = await fetch(`${API_URL}/api/stats/advanced?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAdvancedStats(data);
    } catch (error) {
      toast.error('Error al filtrar');
    }
  };

  const fetchModelInsights = async () => {
    if (modelInsights || modelInsightsLoading) return;
    setModelInsightsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/model-insights?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setModelInsights(await res.json());
    } catch (e) {
      console.error('Failed to fetch model insights:', e);
      toast.error('No se pudo cargar el panel del modelo');
    } finally {
      setModelInsightsLoading(false);
    }
  };


  const fetchVerifiedPicks = async (page = 1) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/stats/verified-picks?page=${page}&limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });      const data = await response.json();
      setVerifiedPicks(data);
    } catch (error) {
      toast.error('Error al cargar picks');
    }
  };

  const exportCSV = () => {
    if (!verifiedPicks?.picks) return;
    
    const headers = ['Fecha', 'Local', 'Visitante', 'Predicción', 'Cuota', 'Resultado', 'Hash'];
    const rows = verifiedPicks.picks.map(p => [
      p.match_date?.substring(0, 10),
      p.home_team,
      p.away_team,
      p.prediction,
      p.odds,
      p.result,
      p.verification_hash
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oyr_picks_${new Date().toISOString().substring(0, 10)}.csv`;
    a.click();
    toast.success('CSV exportado');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00E599]" />
      </div>
    );
  }

  const stats = basicStats?.simple || {};
  const evolution = bankrollEvolution?.evolution || [];
  const summary = bankrollEvolution?.summary || {};

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      
      <main className="pt-16 pb-20 px-3 sm:px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 pt-2">
            <h1 className="font-heading text-2xl font-bold text-white">ESTADÍSTICAS</h1>
            <p className="text-xs text-zinc-400">Track record verificable con hash criptográfico</p>
          </div>

          {/* Main Stats Cards - 5 columns with Yield */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
            <Card className="bg-gradient-to-br from-[#00E599]/20 to-zinc-900 border-[#00E599]/30">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-heading font-bold text-[#00E599]">{stats.win_rate || 0}%</p>
                <p className="text-[10px] text-zinc-400 uppercase">Win Rate</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-[#6366F1]/20 to-zinc-900 border-[#6366F1]/30">
              <CardContent className="p-4 text-center">
                <p className={`text-3xl font-heading font-bold ${stats.roi >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                  {stats.roi >= 0 ? '+' : ''}{stats.roi || 0}%
                </p>
                <p className="text-[10px] text-zinc-400 uppercase">ROI</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-[#F43F5E]/20 to-zinc-900 border-[#F43F5E]/30">
              <CardContent className="p-4 text-center">
                <p className={`text-3xl font-heading font-bold ${(stats.yield || 0) >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                  {(stats.yield || 0) >= 0 ? '+' : ''}{stats.yield || 0}%
                </p>
                <p className="text-[10px] text-zinc-400 uppercase">Yield</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-[#EAB308]/20 to-zinc-900 border-[#EAB308]/30">
              <CardContent className="p-4 text-center">
                <p className={`text-3xl font-heading font-bold ${stats.profit_units >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                  {stats.profit_units >= 0 ? '+' : ''}{stats.profit_units || 0}u
                </p>
                <p className="text-[10px] text-zinc-400 uppercase">Profit</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-heading font-bold text-white">{stats.wins + stats.losses || 0}</p>
                <p className="text-[10px] text-zinc-400 uppercase">Verificados</p>
              </CardContent>
            </Card>
          </div>

          {/* Record Display */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center gap-8">
              <div className="text-center">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#00E599]" />
                  <span className="text-2xl font-heading font-bold text-[#00E599]">{stats.wins || 0}</span>
                </div>
                <p className="text-xs text-zinc-500">Aciertos</p>
              </div>
              <div className="text-4xl text-zinc-600">-</div>
              <div className="text-center">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-[#F43F5E]" />
                  <span className="text-2xl font-heading font-bold text-[#F43F5E]">{stats.losses || 0}</span>
                </div>
                <p className="text-xs text-zinc-500">Fallos</p>
              </div>
            </div>
            
            {/* Streaks */}
            {summary.best_winning_streak > 0 && (
              <div className="flex justify-center gap-4 mt-4 text-xs">
                <Badge className="bg-[#00E599]/20 text-[#00E599] border-0">
                  Mejor racha: {summary.best_winning_streak}W
                </Badge>
                <Badge className="bg-[#F43F5E]/20 text-[#F43F5E] border-0">
                  Peor racha: {summary.worst_losing_streak}L
                </Badge>
                <Badge className={`border-0 ${summary.current_streak > 0 ? 'bg-[#00E599]/20 text-[#00E599]' : 'bg-[#F43F5E]/20 text-[#F43F5E]'}`}>
                  Actual: {summary.current_streak > 0 ? `${summary.current_streak}W` : `${Math.abs(summary.current_streak)}L`}
                </Badge>
              </div>
            )}
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5 bg-zinc-900 h-11 mb-4">
              <TabsTrigger value="overview" className="text-xs data-[state=active]:bg-[#00E599]/20 data-[state=active]:text-[#00E599]">
                <BarChart3 className="w-3 h-3 mr-1" /> Resumen
              </TabsTrigger>
              <TabsTrigger value="evolution" className="text-xs data-[state=active]:bg-[#6366F1]/20 data-[state=active]:text-[#6366F1]">
                <TrendingUp className="w-3 h-3 mr-1" /> Evolución
              </TabsTrigger>
              <TabsTrigger value="model" className="text-xs data-[state=active]:bg-[#F97316]/20 data-[state=active]:text-[#F97316]" onClick={() => fetchModelInsights()} data-testid="tab-model-insights">
                <Target className="w-3 h-3 mr-1" /> Modelo
              </TabsTrigger>
              <TabsTrigger value="filters" className="text-xs data-[state=active]:bg-[#EAB308]/20 data-[state=active]:text-[#EAB308]">
                <Filter className="w-3 h-3 mr-1" /> Filtros
              </TabsTrigger>
              <TabsTrigger value="picks" className="text-xs data-[state=active]:bg-zinc-700 data-[state=active]:text-white" onClick={() => fetchVerifiedPicks()}>
                <Award className="w-3 h-3 mr-1" /> Picks
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              {/* Monthly Performance */}
              <Card className="bg-zinc-900/50 border-zinc-800 mb-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white">Rendimiento Mensual</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {monthlyStats?.monthly?.slice(0, 6).map((month) => (
                      <div key={month.month} className="flex items-center justify-between text-xs bg-zinc-950 rounded p-2">
                        <span className="text-zinc-400 w-20">{month.month}</span>
                        <span className="text-white">{month.wins}W-{month.losses}L</span>
                        <span className={`font-bold ${month.win_rate >= 55 ? 'text-[#00E599]' : month.win_rate >= 50 ? 'text-[#EAB308]' : 'text-[#F43F5E]'}`}>
                          {month.win_rate}%
                        </span>
                        <span className={`font-bold ${month.profit >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                          {month.profit >= 0 ? '+' : ''}{month.profit}u
                        </span>
                        <span className={`text-[10px] ${month.roi >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                          ROI {month.roi >= 0 ? '+' : ''}{month.roi}%
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* By Channel */}
              <div className="grid grid-cols-2 gap-3">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-[#00E599]">Canal FREE</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">
                        {basicStats?.by_channel?.free?.wins || 0}W - {basicStats?.by_channel?.free?.losses || 0}L
                      </p>
                      <p className="text-xs text-zinc-400">
                        {basicStats?.by_channel?.free?.wins + basicStats?.by_channel?.free?.losses > 0 
                          ? `${Math.round(basicStats.by_channel.free.wins / (basicStats.by_channel.free.wins + basicStats.by_channel.free.losses) * 100)}% Win Rate`
                          : 'Sin datos'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-[#EAB308]">Canal VIP</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">
                        {basicStats?.by_channel?.vip?.wins || 0}W - {basicStats?.by_channel?.vip?.losses || 0}L
                      </p>
                      <p className="text-xs text-zinc-400">
                        {basicStats?.by_channel?.vip?.wins + basicStats?.by_channel?.vip?.losses > 0 
                          ? `${Math.round(basicStats.by_channel.vip.wins / (basicStats.by_channel.vip.wins + basicStats.by_channel.vip.losses) * 100)}% Win Rate`
                          : 'Sin datos'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Evolution Tab */}
            <TabsContent value="evolution">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white">Evolución del Bankroll</CardTitle>
                </CardHeader>
                <CardContent>
                  {evolution.length > 0 ? (
                    <div className="space-y-1 max-h-[400px] overflow-y-auto">
                      {evolution.map((day, idx) => (
                        <div key={day.date} className="flex items-center justify-between text-xs bg-zinc-950 rounded px-2 py-1.5">
                          <span className="text-zinc-400 w-24">{day.date}</span>
                          <span className="text-white">{day.daily_wins}W-{day.daily_losses}L</span>
                          <span className={`font-mono ${day.daily_profit >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                            {day.daily_profit >= 0 ? '+' : ''}{day.daily_profit}u
                          </span>
                          <span className={`font-bold ${day.cumulative_profit >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                            Total: {day.cumulative_profit >= 0 ? '+' : ''}{day.cumulative_profit}u
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-zinc-500 py-8">Sin datos de evolución</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Model Insights Tab — CLV + Line Shopping */}
            <TabsContent value="model">
              {modelInsightsLoading && !modelInsights ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="w-8 h-8 animate-spin text-[#F97316]" />
                </div>
              ) : !modelInsights ? (
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="py-10 text-center text-zinc-400">
                    Pulsa la pestaña Modelo para cargar el análisis.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4" data-testid="model-insights-panel">
                  {/* Top Row — 3 KPI cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Global CLV */}
                    <Card className={`bg-zinc-900/50 border ${
                      modelInsights.clv?.global_status === 'green' ? 'border-[#00E599]/40' :
                      modelInsights.clv?.global_status === 'red' ? 'border-[#F43F5E]/40' :
                      'border-zinc-700'
                    }`}>
                      <CardContent className="pt-4">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">CLV Global (30d)</div>
                        <div className={`text-3xl font-bold ${
                          (modelInsights.clv?.global_clv_avg || 0) >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'
                        }`}>
                          {(modelInsights.clv?.global_clv_avg || 0) > 0 ? '+' : ''}{modelInsights.clv?.global_clv_avg || 0}%
                        </div>
                        <div className="text-xs text-zinc-400 mt-1">
                          {modelInsights.clv?.total_picks || 0} picks · {modelInsights.clv?.positive_clv_pct || 0}% positivos
                        </div>
                      </CardContent>
                    </Card>

                    {/* Line shopping impact */}
                    <Card className="bg-zinc-900/50 border-[#6366F1]/40">
                      <CardContent className="pt-4">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Line Shopping</div>
                        <div className="text-3xl font-bold text-[#6366F1]">
                          +{modelInsights.line_shopping?.extra_units_won || 0}u
                        </div>
                        <div className="text-xs text-zinc-400 mt-1">
                          {modelInsights.line_shopping?.picks_improved || 0} picks · +{modelInsights.line_shopping?.avg_improvement_pct || 0}% cuota media
                        </div>
                      </CardContent>
                    </Card>

                    {/* Positive CLV pct */}
                    <Card className="bg-zinc-900/50 border-zinc-700">
                      <CardContent className="pt-4">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Picks Ganadores</div>
                        <div className="text-3xl font-bold text-white">
                          {modelInsights.clv?.won_avg_clv ?? '—'}%
                        </div>
                        <div className="text-xs text-zinc-400 mt-1">CLV medio en aciertos</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Markets breakdown */}
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-white flex items-center gap-2">
                        <Target className="w-4 h-4" /> CLV por Mercado
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {(modelInsights.clv?.by_market || []).map((m) => (
                          <div key={m.market} className="flex items-center justify-between p-2 bg-zinc-950/50 rounded"
                               data-testid={`market-row-${m.market}`}>
                            <div className="flex items-center gap-2">
                              <Badge className={
                                m.status === 'green' ? 'bg-[#00E599]/20 text-[#00E599] border-[#00E599]/30' :
                                m.status === 'red' ? 'bg-[#F43F5E]/20 text-[#F43F5E] border-[#F43F5E]/30' :
                                m.status === 'amber' ? 'bg-[#EAB308]/20 text-[#EAB308] border-[#EAB308]/30' :
                                'bg-zinc-800 text-zinc-400 border-zinc-700'
                              }>
                                {m.status === 'green' ? '✅' : m.status === 'red' ? '🚨' : m.status === 'amber' ? '⚠️' : 'ℹ️'}
                              </Badge>
                              <span className="text-sm text-white font-medium">{m.market}</span>
                              <span className="text-xs text-zinc-500">n={m.sample}</span>
                            </div>
                            <div className={`text-sm font-bold ${
                              m.avg_clv >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'
                            }`}>
                              {m.avg_clv > 0 ? '+' : ''}{m.avg_clv}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recommendations */}
                  {(modelInsights.clv?.recommendations || []).length > 0 && (
                    <Card className="bg-zinc-900/50 border-[#F97316]/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-[#F97316]">Recomendaciones activas</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2 text-sm text-zinc-300">
                          {modelInsights.clv.recommendations.map((r, i) => (
                            <li key={i} className="p-2 bg-zinc-950/50 rounded">{r.action}</li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </TabsContent>


            {/* Filters Tab */}
            <TabsContent value="filters">
              <Card className="bg-zinc-900/50 border-zinc-800 mb-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white">Filtrar Estadísticas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    <Select value={filters.league || "all"} onValueChange={(v) => setFilters({...filters, league: v === "all" ? "" : v})}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-700 h-9 text-xs">
                        <SelectValue placeholder="Liga" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="all">Todas</SelectItem>
                        {Object.entries(leagueLabels).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Select value={filters.channel || "all"} onValueChange={(v) => setFilters({...filters, channel: v === "all" ? "" : v})}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-700 h-9 text-xs">
                        <SelectValue placeholder="Canal" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="free">FREE</SelectItem>
                        <SelectItem value="vip">VIP</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={filters.market || "all"} onValueChange={(v) => setFilters({...filters, market: v === "all" ? "" : v})}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-700 h-9 text-xs">
                        <SelectValue placeholder="Mercado" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="over">Over/Under</SelectItem>
                        <SelectItem value="btts">BTTS</SelectItem>
                        <SelectItem value="1x2">1X2</SelectItem>
                        <SelectItem value="handicap">Hándicap</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button onClick={fetchAdvancedStats} className="w-full btn-primary h-10">
                    <Filter className="w-4 h-4 mr-2" /> Aplicar Filtros
                  </Button>
                </CardContent>
              </Card>

              {advancedStats && (
                <div className="space-y-4">
                  {/* Summary */}
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-white">Resultados Filtrados</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div>
                          <p className="text-xl font-bold text-white">{advancedStats.summary.total_picks}</p>
                          <p className="text-[10px] text-zinc-500">Picks</p>
                        </div>
                        <div>
                          <p className="text-xl font-bold text-[#00E599]">{advancedStats.summary.win_rate}%</p>
                          <p className="text-[10px] text-zinc-500">Win Rate</p>
                        </div>
                        <div>
                          <p className={`text-xl font-bold ${advancedStats.summary.profit_units >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                            {advancedStats.summary.profit_units >= 0 ? '+' : ''}{advancedStats.summary.profit_units}u
                          </p>
                          <p className="text-[10px] text-zinc-500">Profit</p>
                        </div>
                        <div>
                          <p className={`text-xl font-bold ${advancedStats.summary.roi >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                            {advancedStats.summary.roi >= 0 ? '+' : ''}{advancedStats.summary.roi}%
                          </p>
                          <p className="text-[10px] text-zinc-500">ROI</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* By Odds Range */}
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-white">Por Rango de Cuota</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {Object.entries(advancedStats.by_odds_range || {}).map(([range, data]) => (
                          <div key={range} className="flex items-center justify-between text-xs bg-zinc-950 rounded p-2">
                            <span className="text-zinc-400 w-20">{range}</span>
                            <span className="text-white">{data.wins}W-{data.losses}L</span>
                            <span className={`font-bold ${data.win_rate >= 50 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                              {data.win_rate}%
                            </span>
                            <span className={`${data.profit >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
                              {data.profit >= 0 ? '+' : ''}{data.profit}u
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Verified Picks Tab */}
            <TabsContent value="picks">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-bold text-white">Picks Verificados con Hash</h3>
                <Button variant="outline" size="sm" onClick={exportCSV} className="h-8 text-xs border-zinc-700">
                  <Download className="w-3 h-3 mr-1" /> Exportar CSV
                </Button>
              </div>
              
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {verifiedPicks?.picks?.map((pick) => (
                  <div key={pick.id} className={`rounded-lg p-3 border ${
                    pick.result === 'won' ? 'bg-[#00E599]/10 border-[#00E599]/30' : 'bg-[#F43F5E]/10 border-[#F43F5E]/30'
                  }`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {pick.home_team} vs {pick.away_team}
                        </p>
                        <p className="text-xs text-zinc-400">{pick.prediction} @{pick.odds?.toFixed(2)}</p>
                        <p className="text-[10px] text-zinc-500">{pick.match_date?.substring(0, 10)}</p>
                      </div>
                      <Badge className={pick.result === 'won' ? 'bg-[#00E599]/20 text-[#00E599]' : 'bg-[#F43F5E]/20 text-[#F43F5E]'}>
                        {pick.result === 'won' ? 'ACIERTO' : 'FALLO'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-[9px] text-zinc-500">
                      <span>🔐 Hash: <code className="bg-zinc-950 px-1 rounded">{pick.verification_hash?.substring(0, 16)}...</code></span>
                    </div>
                  </div>
                ))}
              </div>
              
              {verifiedPicks?.pagination && (
                <div className="flex justify-center gap-2 mt-4">
                  {verifiedPicks.pagination.page > 1 && (
                    <Button variant="outline" size="sm" onClick={() => fetchVerifiedPicks(verifiedPicks.pagination.page - 1)}>
                      Anterior
                    </Button>
                  )}
                  <span className="text-xs text-zinc-400 py-2">
                    Página {verifiedPicks.pagination.page} de {verifiedPicks.pagination.pages}
                  </span>
                  {verifiedPicks.pagination.page < verifiedPicks.pagination.pages && (
                    <Button variant="outline" size="sm" onClick={() => fetchVerifiedPicks(verifiedPicks.pagination.page + 1)}>
                      Siguiente
                    </Button>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
